/**
 * Every `module` / `action` / `reference_kind` the API writes into `audit_logs`, grouped by the
 * module that emits it. The raw values are internal identifiers (snake_case); the screen never
 * shows them: each has a Spanish label under `audit_labels.*` in `locales/es.json`.
 *
 * When a service starts recording a new action, add it here AND to `audit_labels.action`
 * (`audit-catalog.test.ts` fails if a catalog entry has no translation). A value that is not
 * in the catalog still renders readably through `humanizeAuditValue`.
 */
export const AUDIT_CATALOG: Record<string, readonly string[]> = {
  sales: [
    'cashier_create_medical_order',
    'cashier_reassign_pending_consulting_room',
    'cashier_discard_opening_order',
    'cashier_send_to_doctor',
    'doctor_take_order',
    'doctor_send_to_cashier_for_charge',
    'doctor_return_to_cashier_for_correction',
    'set_sale_order_current_weight',
    'clear_sale_order_current_weight',
    'apply_discount',
    'authorized_line_price_override',
    'charge_sale',
    'return_to_doctor',
    'cancel_sale',
    'add_open_account_concepts',
  ],
  cash: ['shift_open', 'shift_close', 'cash_in', 'cash_out', 'surprise_count'],
  open_accounts: ['apply_abono', 'apply_customer_abono', 'cancel_last_movement', 'settle'],
  invoicing: ['request_cfdi', 'request_cfdi_failed', 'cancel_cfdi', 'cancel_cfdi_failed'],
  inventory: ['kit_item_price_override_changed'],
  customers: ['secondary_phone_added', 'secondary_phone_removed'],
  users: ['set_user_photo', 'remove_user_photo'],
  customer_portal: [
    'customer_register',
    'customer_verify',
    'customer_login',
    'customer_account_delete',
    'staff_resolve_pet_qr',
  ],
  appointments: [
    'appointment_requested',
    'appointment_booked',
    'appointment_confirmed',
    'appointment_checked_in',
    'appointment_started',
    'appointment_completed',
    'appointment_no_show',
    'appointment_cancelled',
    'appointment_rescheduled',
    'appointment_moved',
    'appointment_reverted',
  ],
  hospitalization: [
    'hospitalization_admitted',
    'hospitalization_discharged',
    'hospitalization_deceased',
    'hospitalization_transferred',
    'hospitalization_cancelled',
    'hospitalization_reopened',
    'hospitalization_location_changed',
    'hospitalization_note_added',
    'hospitalization_note_voided',
    'hospitalization_vital_recorded',
    'hospitalization_vital_voided',
  ],
  vaccination_card: [
    'vaccination_entry_from_sale',
    'vaccination_entry_manual_added',
    'vaccination_entry_voided',
  ],
  clinical: [
    'clinical_visit_created',
    'clinical_visit_chief_complaint_updated',
    'clinical_anamnesis_saved',
    'clinical_physical_exam_saved',
    'clinical_plan_treatment_saved',
    'clinical_diagnosis_added',
    'clinical_diagnosis_updated',
    'clinical_diagnosis_removed',
    'clinical_visit_pending_completion',
    'clinical_visit_closed',
    'clinical_addendum_added',
    'clinical_visit_voided',
    'clinical_visit_cancelled',
    'clinical_attachment_registered',
    'clinical_attachment_uploaded',
    'clinical_attachment_reviewed',
    'clinical_attachment_archived',
    'clinical_lab_request_created',
    'clinical_lab_request_status_updated',
    'clinical_lab_result_recorded',
    'clinical_lab_result_updated',
    'clinical_imaging_study_recorded',
    'clinical_imaging_study_interpreted',
    'clinical_prescription_created',
    'clinical_prescription_line_added',
    'clinical_prescription_line_updated',
    'clinical_prescription_line_removed',
    'clinical_prescription_notes_updated',
    'clinical_prescription_activated',
    'clinical_prescription_discontinued',
    'clinical_prescription_completed',
    'clinical_voice_note_transcribed',
    'clinical_voice_note_applied',
    'clinical_voice_note_forgotten',
  ],
};

export const AUDIT_MODULES: readonly string[] = Object.keys(AUDIT_CATALOG);

export const AUDIT_ALL_ACTIONS: readonly string[] = AUDIT_MODULES.flatMap(
  (m) => AUDIT_CATALOG[m] ?? [],
);

export const AUDIT_REFERENCE_KINDS: readonly string[] = [
  'sale_order',
  'sale_order_item',
  'invoice',
  'customer',
  'customer_account',
  'pet',
  'user',
  'open_account',
  'open_account_movement',
  'cash_shift',
  'cash_movement',
  'kit_item',
  'appointment',
  'hospitalization_episode',
  'hospitalization_progress_note',
  'hospitalization_vital_sign',
  'vaccination_card',
  'vaccination_card_entry',
  'clinical_visit',
  'clinical_diagnosis',
  'clinical_attachment',
  'clinical_lab_request',
  'clinical_lab_result',
  'clinical_imaging_study',
  'clinical_prescription',
  'clinical_prescription_line',
  'clinical_voice_note',
];

/** Readable fallback for a value with no translation: `foo_bar` → `Foo bar`. Never the raw id. */
export function humanizeAuditValue(raw: string): string {
  const spaced = raw.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
